import { api } from '../api';
import { useApi } from '../lib/useApi';
import { AreaChart } from '../lib/charts';
import Drawer from '../components/Drawer';
import { Card, CardHead, ErrorState, limitLabel, Loading, PlanBadge, Usage } from '../components/ui';
import { daysBetween, fmtDate, gb, gbBytes, hrs, n0, pct, plural, rub } from '../lib/format';
import { STATUS } from '../lib/plans';
import { useAuth } from '../components/AuthProvider';
import { useAction } from '../lib/useAction';
import { loadPlans } from '../lib/plans';
import { useState } from 'react';

const byCode = (usage, code) => (usage || []).find((u) => u.code === code) || { used: 0, max: 0 };

export default function WorkspaceDrawer({ id, onClose, onOpenUser }) {
  return (
    <Drawer open={!!id} onClose={onClose}>
      {id ? <Body id={id} onClose={onClose} onOpenUser={onOpenUser} /> : null}
    </Drawer>
  );
}

function Body({ id, onClose, onOpenUser }) {
  const { canEdit } = useAuth();
  const { data: o, loading, error, reload } = useApi(() => api.workspace(id), [id]);
  const series = useApi(() => api.workspaceSeries(id, { days: 60 }), [id]);

  if (loading) return <div className="dr-body"><Loading what="пространство" /></div>;
  if (error) return <div className="dr-body"><ErrorState error={error} onRetry={reload} /></div>;

  const status = o.retention ? 'RETENTION' : o.subscription?.status;
  const st = STATUS[status] || { ru: status || 'нет подписки', cls: 'b-expired' };
  const seats = byCode(o.usage, 'AVAILABLE_USER_COUNT');
  const space = byCode(o.usage, 'AVAILABLE_SPACE_MB');
  const rate = space.max ? space.used / space.max : 0;
  const mailto = `mailto:${(o.owners || [])[0] || ''}?subject=${encodeURIComponent('7cut · ' + o.title)}&body=${encodeURIComponent('Здравствуйте!\n\n')}`;

  return (
    <>
      <div className="dr-head">
        <button className="close" onClick={onClose} aria-label="закрыть">✕</button>
        <h2>{o.title}</h2>
        <div>
          <PlanBadge plan={o.plan?.name || '—'} published={o.plan ? o.plan.published : true} />{' '}
          <span className={`badge ${st.cls}`}>{st.ru}</span>
          <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
            РП №{o.id} · создано {fmtDate(o.createdAt)}
          </span>
        </div>
      </div>

      <div className="dr-body">
        <div className="grid cards-2">
          <Card>
            <div className="k-label">людей в команде</div>
            <div className="k-val" style={{ fontSize: 30 }}>
              {n0(o.members?.active)}<small>{limitLabel(seats.max, seats.unlimited)}</small>
            </div>
            <div style={{ marginTop: 10 }}><Usage used={o.members?.active ?? seats.used} max={seats.max} unlimited={seats.unlimited} /></div>
          </Card>
          <Card>
            <div className="k-label">занято места</div>
            <div className="k-val" style={{ fontSize: 30 }}>
              {gbBytes(o.sizeBytes)}<small>{limitLabel(space.max, space.unlimited, gb)}</small>
            </div>
            <div style={{ marginTop: 10 }}><Usage used={space.used ?? o.sizeBytes / (1024 * 1024)} max={space.max} unlimited={space.unlimited} fmt={gb} updating={space.updating} /></div>
          </Card>
        </div>

        {rate > 0.85 ? (
          <div className="note warn">
            Место почти кончилось — {pct(rate)}.{' '}
            {o.plan.name === 'База'
              ? 'На «Базе» докупить нельзя, только переход на ПРО.'
              : 'Можно предложить докупку: +299 ₽ за 10 ГБ.'}
          </div>
        ) : null}

        <div>
          <h3 className="dr-h">подписка</h3>
          <dl className="kv">
            <dt>тариф</dt>
            <dd>
              {o.plan?.name || '—'}
              {o.subscription?.status === 'ACTIVE' ? (o.subscription.annual ? ', годовая оплата' : ', помесячно') : ''}
              {o.plan && !o.plan.published ? <div className="t-sub">не на витрине — подписку выдают по прямой ссылке</div> : null}
            </dd>
            <dt>состояние</dt>
            <dd><span className={`badge ${st.cls}`}>{st.ru}</span></dd>
            {o.subscription?.trialEndDate ? (
              <>
                <dt>триал до</dt>
                <dd>{fmtDate(o.subscription.trialEndDate)} — осталось{' '}
                  {Math.max(0, daysBetween(o.subscription.trialEndDate, new Date()))}{' '}
                  {plural(daysBetween(o.subscription.trialEndDate, new Date()), 'день', 'дня', 'дней')}</dd>
              </>
            ) : null}
            {o.subscription?.endDate ? (
              <>
                <dt>подписка до</dt>
                <dd>
                  {fmtDate(o.subscription.endDate)}
                  {o.monthlyRevenue ? ` · ${rub(o.monthlyRevenue)}` : ''}
                  {o.subscription.autoRenew === false ? <div className="t-sub">автопродление выключено</div> : null}
                </dd>
              </>
            ) : null}
            {o.retention ? (
              <><dt>удаление данных</dt><dd style={{ color: 'var(--bad)' }}>{fmtDate(o.retention.deadlineAt)}</dd></>
            ) : null}
            <dt>доплаты</dt>
            <dd>
              {o.addonsMonthly ? `${rub(o.addonsMonthly)} в месяц сверх тарифа` : <span className="muted">нет</span>}
            </dd>
            <dt>в месяц</dt>
            <dd className="mono">{o.monthlyRevenue ? rub(o.monthlyRevenue) : <span className="muted">не платит</span>}</dd>
            <dt>платежи</dt>
            <dd>
              {o.payments && o.payments.count
                ? <>
                    {n0(o.payments.count)} {plural(o.payments.count, 'успешный', 'успешных', 'успешных')}
                    {o.payments.total ? ` · ${rub(o.payments.total)} всего` : ''}
                    {o.payments.lastAt ? <div className="t-sub">последний {fmtDate(o.payments.lastAt)}</div> : null}
                  </>
                : <span className="muted">
                    не было{o.monthlyRevenue ? ' — подписка выдана вручную' : ''}
                  </span>}
            </dd>
            <dt>владелец</dt>
            <dd>{(o.owners || []).map((e) => <div key={e}>{e}</div>)}</dd>
          </dl>
        </div>

        <Card className="pad">
          <CardHead title="комментарии за 60 дней" caption={`всего за жизнь РП — ${n0(o.comments)}`} />
          {series.loading ? <Loading what="график" />
            : series.error ? <ErrorState error={series.error} onRetry={series.reload} />
            : <AreaChart data={series.data.map((d) => ({ date: d.date, v: d.value }))} height={130} />}
        </Card>

        <div className="grid cards-3">
          <Card><div className="k-label">проектов</div>
            <div className="k-val" style={{ fontSize: 26 }}>
              {n0(o.projects)}
              <small>{limitLabel(byCode(o.usage, 'AVAILABLE_PROJECT_COUNT').max, byCode(o.usage, 'AVAILABLE_PROJECT_COUNT').unlimited)}</small>
            </div></Card>
          <Card><div className="k-label">видео</div>
            <div className="k-val" style={{ fontSize: 26 }}>{n0(o.videos)}</div></Card>
          <Card><div className="k-label">хронометраж</div>
            <div className="k-val" style={{ fontSize: 26 }}>{hrs(o.durationSec)}</div></Card>
        </div>

        <div>
          <h3 className="dr-h">команда</h3>
          <div className="list">
            {(o.team || []).map((u) => (
              <button className="row" key={u.id} onClick={() => onOpenUser && onOpenUser(u.id)}>
                <span className="rn">
                  {u.firstName} {u.lastName} {u.blocked ? <span className="badge b-blocked">блок</span> : null}
                  <div className="t-sub">{u.email}</div>
                </span>
                <span className="rv">
                  {u.role === 'OWNER' ? 'владелец' : 'редактор'}{u.status === 'PENDING' ? ' · не вошёл' : ''}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="dr-h">действия</h3>
          <div className="act">
            <a className="btn" href={mailto}>написать владельцу</a>
            <CopyButton value={(o.owners || []).join(', ')} />
          </div>

          {canEdit ? <WorkspaceActions workspace={o} onDone={reload} /> : (
            <div className="note" style={{ marginTop: 12 }}>У вас доступ только на чтение.</div>
          )}

          <div className="note" style={{ marginTop: 12 }}>
            «Написать владельцу» открывает почтовую программу с подставленным адресом и темой —
            письмо уходит лично от вас.
          </div>
        </div>
      </div>
    </>
  );
}

function CopyButton({ value }) {
  return (
    <button
      className="btn"
      onClick={(e) => {
        navigator.clipboard?.writeText(value);
        const btn = e.currentTarget;
        const text = btn.textContent;
        btn.textContent = 'скопировано';
        setTimeout(() => { btn.textContent = text; }, 1600);
      }}
    >
      скопировать почту
    </button>
  );
}

/** Действия над подпиской. Все — существующие эндпоинты приложения. */
function WorkspaceActions({ workspace, onDone }) {
  const plans = useApi(() => loadPlans(api), []);
  const [priceId, setPriceId] = useState('');
  const [status, setStatus] = useState('');

  const assign = useAction(() => api.actions.assignPlan(workspace.id, Number(priceId)), { onDone });
  const changeStatus = useAction(
    () => api.actions.setSubscriptionStatus(workspace.subscription.id, status), { onDone });
  const remove = useAction(
    () => api.actions.deleteSubscription(workspace.subscription.id), { onDone });

  const options = [];
  (plans.data || []).forEach((plan) => {
    (plan.prices || []).forEach((price) => {
      const period = price.periodUnit === 'YEAR' ? 'год' : price.periodValue > 1
        ? `${price.periodValue} мес` : 'мес';
      options.push({
        id: price.id,
        label: `${plan.name} — ${n0(price.price)} ₽ / ${period}${plan.onSite === false ? ' · не на витрине' : ''}`,
      });
    });
  });

  return (
    <>
      <div className="act" style={{ marginTop: 10 }}>
        <select className="btn" value={priceId} onChange={(e) => { setPriceId(e.target.value); assign.reset(); }}>
          <option value="">сменить тариф…</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        {priceId ? (
          <button className={`btn${assign.state === 'armed' ? ' danger' : ''}`} onClick={() => assign.fire()}>
            {assign.state === 'armed' ? 'точно назначить?' : assign.state === 'busy' ? 'назначаем…'
              : assign.state === 'done' ? 'назначено' : 'назначить'}
          </button>
        ) : null}
      </div>

      {workspace.subscription ? (
        <div className="act" style={{ marginTop: 8 }}>
          <select className="btn" value={status} onChange={(e) => { setStatus(e.target.value); changeStatus.reset(); }}>
            <option value="">сменить состояние…</option>
            {['ACTIVE', 'TRIALING', 'UNPAID', 'EXPIRED', 'CANCELLED'].map((st) => (
              <option key={st} value={st}>{STATUS[st]?.ru || st}</option>
            ))}
          </select>
          {status ? (
            <button className={`btn${changeStatus.state === 'armed' ? ' danger' : ''}`} onClick={() => changeStatus.fire()}>
              {changeStatus.state === 'armed' ? 'точно сменить?' : changeStatus.state === 'busy' ? 'меняем…'
                : changeStatus.state === 'done' ? 'готово' : 'применить'}
            </button>
          ) : null}
          <button className="btn danger" onClick={() => remove.fire()}>
            {remove.state === 'armed' ? 'точно удалить подписку?' : remove.state === 'busy' ? 'удаляем…'
              : remove.state === 'done' ? 'удалена' : 'удалить подписку'}
          </button>
        </div>
      ) : null}

      {assign.error || changeStatus.error || remove.error ? (
        <div className="note" style={{ marginTop: 10, borderColor: 'var(--bad)', color: 'var(--bad)' }}>
          {(assign.error || changeStatus.error || remove.error).message}
        </div>
      ) : null}

      <div className="note" style={{ marginTop: 10 }}>
        Смена тарифа назначает пространству новую подписку на выбранную цену —
        тем же способом, каким подписку оформляет само приложение.
        Каждое действие срабатывает со второго нажатия.
      </div>
    </>
  );
}
